import React from 'react';
import './LaunchingForm.styles.css';
import { withStyles, WithStyles } from '@material-ui/core/styles';
import { Form } from 'react-bootstrap';
import { connect } from 'react-redux';
import Modal from '../Modal';
import { openErrorModal } from '../../actions';
import useLaunchingForm from './useLaunchingForm';
import LegalInfoSection from './sections/LegalInfoSection';
import ShopInfoSection from './sections/ShopInfoSection';
import ConfigInfoSection from './sections/ConfigInfoSection';
import { UserData } from './LaunchingForm.types';
import { normalizeVatNumber, validatePhoneNumber } from './LaunchingForm.services';
import { phoneNumberMinLength } from '../../utils/shopUtils';
import { validateEmail } from './validation.schema';

const styles = () => ({});

interface LaunchingFormProps extends WithStyles<typeof styles> {
  user?: UserData;
  showLaunchingModal: boolean;
  onClose: () => void;
  openErrorModal: (message: string) => void;
}

function LaunchingForm(props: LaunchingFormProps) {
  const { user, showLaunchingModal, onClose, openErrorModal } = props;

  const {
    formData,
    errors,
    isSubmitting,
    isPhoneIncorrectRef,
    handleSelectAddress,
    handleSubmit,
    handleFieldChange,
    validateField,
    resetState,
  } = useLaunchingForm({ user, showLaunchingModal, openErrorModal });

  const hasErrors =
    (errors.legal && Object.keys(errors.legal).length > 0) ||
    (errors.shop && Object.keys(errors.shop).length > 0) ||
    (errors.configurations && Object.keys(errors.configurations).length > 0);

  const hasValidLegalAddress = !!(
    formData.legal.address &&
    formData.legal.address.city &&
    formData.legal.address.city.trim() !== '' &&
    formData.legal.address.postalCode &&
    formData.legal.address.postalCode.trim() !== ''
  );

  const hasValidShopAddress = !!(
    formData.shop.address &&
    formData.shop.address.city &&
    formData.shop.address.city.trim() !== '' &&
    formData.shop.address.postalCode &&
    formData.shop.address.postalCode.trim() !== ''
  );

  const hasValidEmail = !!(
    formData.shop.email &&
    formData.shop.email.trim() !== '' &&
    !validateEmail(formData.shop.email)
  );

  const hasAllRequiredFields =
    formData.legal.companyName?.trim() &&
    formData.legal.companyType?.trim() &&
    formData.legal.vatNumber?.trim() &&
    formData.legal.registreCommerceNumero?.trim() &&
    formData.legal.registreCommerceVille?.trim() &&
    formData.legal.capitalSocial &&
    hasValidLegalAddress &&
    formData.shop.name?.trim() &&
    hasValidEmail &&
    formData.shop.number?.trim() &&
    hasValidShopAddress &&
    formData.configurations.businessType?.trim();

  const isDisabled = hasErrors || !hasAllRequiredFields || isPhoneIncorrectRef.current || isSubmitting;

  const setPhoneValidityFromLib = (value: string, countryCode: { dialCode: string; iso2: string }) => {
    const isIncorrect = validatePhoneNumber(value, countryCode, phoneNumberMinLength);
    isPhoneIncorrectRef.current = isIncorrect;
    return true;
  };

  return (
    <>
      <Modal
        isOpen={showLaunchingModal}
        title='Lancement de votre magasin'
        fixed
        header
        btnTxt='Enregistrer'
        showCancelBtn
        handleClose={() => {
          onClose();
          resetState();
        }}
        onClick={handleSubmit}
        loading={isSubmitting}
        disabled={isDisabled}
      >
        <>
          <Form onSubmit={handleSubmit}>
            <LegalInfoSection
              errors={errors}
              onSelectCompanyAddress={(address, placeId) => handleSelectAddress(address, placeId, 'legal.address', 'legal.typedAddress', false)}
              formData={formData}
              onFieldChange={handleFieldChange}
              validateField={validateField}
              normalizeVatNumber={normalizeVatNumber}
            />
            <ShopInfoSection
              handleSelectAddressShop={(address, placeId) => handleSelectAddress(address, placeId, 'shop.address', 'shop.typedAddress', true)}
              errors={errors}
              setPhoneValidityFromLib={setPhoneValidityFromLib}
              isPhoneIncorrect={isPhoneIncorrectRef.current}
              formData={formData}
              onFieldChange={handleFieldChange}
              validateField={validateField}
            />
            <ConfigInfoSection
              errors={errors}
              formData={formData}
              onFieldChange={handleFieldChange}
              validateField={validateField}
            />
          </Form>
        </>
      </Modal>
    </>
  );
}

function mapStateToProps({ user }: { user?: UserData }) {
  return { user };
}

export default withStyles(styles)(connect(mapStateToProps, { openErrorModal })(LaunchingForm));
